import React, { useState } from "react";
import { Icon, Typography, makeStyles } from "@material-ui/core";
import { Box, Collapse, IconButton } from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { DisplaySection } from "app/main/utils/uiUtils";
import { formatNumber, formatCurrency } from "app/main/utils/tableUtils";
import moment from "moment";
import { useDispatch } from "react-redux";
import { openLoadedFormDialog } from "app/store/tools/formDialogSlice";

const useStyles = makeStyles((theme) => ({
	dataRow: {
		display: "flex",
		flexDirection: "row",
		borderBottom: "1px solid",
		borderColor: theme.palette.divider,
	},
	label: {
		flex: 1,
		textAlign: "left",
		marginTop: "10px",
	},
	value: {
		textAlign: "right",
		marginTop: "10px",
		fontWeight: 600,
		whiteSpace: "nowrap",
	},
	good: {
		color: theme.palette.success.main,
	},
	bad: {
		color: theme.palette.error.main,
	},
	normal: {
		color: theme.palette.primary.dark,
	},
}));

function LoadStrategyMatchView({ loadToUse, variant, strategyId, truckId, carrierId }) {
	const classes = useStyles();
	const [isExpanded, setIsExpanded] = useState(false);
	const dispatch = useDispatch();

	if (!loadToUse || strategyId === null) {
		return null;
	}

	const handleViewStrategy = () => {
		if (strategyId) {
			dispatch(
				openLoadedFormDialog({
					viewId: "STRATEGY_VISUALIZER",
					dataIds: {
						strategyId: strategyId,
						truckId: truckId,
						carrierId: carrierId,
						entryPoint: "savedList",
					},
				})
			);
		}
	};

	const renderDataItem = (label, value, type, info) => {
		return (
			<div className={"flex flex-row border-b-1 border-grey-300 "}>
				<Typography component={"div"} className="flex w-full text-black text-12 ml:text-13 break-normal mt-10">
					{label}
				</Typography>

				<Typography
					component={"div"}
					className={`${classes.value} text-12 ml:text-13 
						${type === "BAD" ? classes.bad : type === "GOOD" ? classes.good : classes.normal}`}
				>
					{info}
					{value === null || value === undefined ? "-" : value}
				</Typography>
			</div>
		);
	};

	const getMatchType = () => {
		if (loadToUse.price_match_strategy) return "Price Match";
		if (loadToUse.fuzzy_match_strategy) return "Fuzzy Match";
		if (loadToUse.match_strategy) return "Strategy Match";
		return "No Match";
	};

	const getMatchDescription = () => {
		return (
			moment(loadToUse.move_date).format("MM/DD") +
			" (" +
			(loadToUse.move_o_city || loadToUse.move_o) +
			"->" +
			(loadToUse.move_d_city || loadToUse.move_d) +
			") RPM " +
			formatCurrency(loadToUse.move_rpm, 2)
		);
	};

	const renderMoveDetails = () => {
		return (
			<>
				{renderDataItem("Match Type", getMatchType())}
				{loadToUse?.match_strategy &&
					renderDataItem(
						"Lane",
						getMatchDescription(),
						null,
						strategyId && (
							<IconButton size="small" onClick={handleViewStrategy}>
								<Icon className="text-13 text-gray-600">visibility</Icon>
							</IconButton>
						)
					)}
			</>
		);
	};

	const renderMatchMetrics = () => {
		return (
			<>
				{loadToUse.strategy_dho !== undefined &&
					renderDataItem(
						"Origin Deadhead",
						<div className="flex items-center">
							<span>{`${formatNumber(loadToUse.strategy_dho)} mi`}</span>
						</div>,
						loadToUse.strategy_dho > 250 ? "BAD" : loadToUse.strategy_dho > 100 ? "NORMAL" : "GOOD"
					)}

				{loadToUse.strategy_dhd !== undefined &&
					renderDataItem(
						"Destination Deadhead",
						`${formatNumber(loadToUse.strategy_dhd)} mi`,
						loadToUse.strategy_dhd > 250 ? "BAD" : loadToUse.strategy_dhd > 100 ? "NORMAL" : "GOOD"
					)}

				{loadToUse.price_delta !== undefined &&
					renderDataItem(
						"Price: ",
						`${formatCurrency(loadToUse.move_payment, 0)} (${formatCurrency(loadToUse.price_delta, 0)})`,
						loadToUse.price_delta < -150 ? "BAD" : loadToUse.price_delta < 0 ? "NORMAL" : "GOOD"
					)}

				{loadToUse.date_delta !== undefined &&
					renderDataItem(
						"Date Difference",
						`${formatNumber(loadToUse.date_delta)} hours`,
						loadToUse.date_delta > 24 ? "BAD" : loadToUse.date_delta > 12 ? "NORMAL" : "GOOD"
					)}

				{loadToUse.miles_delta !== undefined &&
					renderDataItem(
						"Loaded Miles",
						`${formatNumber(loadToUse.move_miles, 0)} mi (${formatNumber(loadToUse.miles_delta, 0)})`,
						loadToUse.miles_delta > 250 ? "BAD" : loadToUse.miles_delta > 0 ? "NORMAL" : "GOOD"
					)}

				{loadToUse.bearing_delta !== undefined &&
					renderDataItem(
						"Bearing Delta",
						`${formatNumber(loadToUse.bearing_delta)}°`,
						loadToUse.bearing_delta > 60 ? "BAD" : loadToUse.bearing_delta > 45 ? "NORMAL" : "GOOD"
					)}
			</>
		);
	};

	return (
		<DisplaySection
			title={
				<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
					<Typography variant="subtitle">Lane Match</Typography>
					<IconButton size="small" onClick={() => setIsExpanded(!isExpanded)} sx={{ p: 0 }}>
						{isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
					</IconButton>
				</Box>
			}
			classes={{ root: "mx-0 mt-0" }}
		>
			<Collapse in={isExpanded}>
				<div className={`flex flex-col ${variant === "skinny" ? "px-2" : "px-4"} py-2`}>
					{renderMoveDetails()}
					{loadToUse?.match_strategy && renderMatchMetrics()}
				</div>
			</Collapse>
		</DisplaySection>
	);
}

export default LoadStrategyMatchView;
